'use client';

import React, { useState, useEffect } from 'react';
import EventCard from './EventCard';
import EventFilters from './EventFilters';
import EventCalendar from './EventCalendar';
import UpcomingEventsList from './UpcomingEventsList';
import EventStats from './EventStats';
import Icon from '@/components/ui/AppIcon';
import Footer from '../../components/Footer';
import Image from 'next/image';
import { createClient } from '@/utils/supabase/client';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  category: string;
  image: string;
  description: string;
  registrationStatus: 'open' | 'closed' | 'full';
}

const EventsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);

    const fetchEvents = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .order('date', { ascending: true });

        if (error) {
          console.error('events fetch error:', error);
          throw error;
        }

        if (data) {
          const mappedEvents: Event[] = data.map((e: any) => ({
            id: e.id.toString(),
            title: e.title || '',
            date: e.date || '',
            time: e.time || '',
            location: e.location || '',
            category: e.category || 'community',
            image: e.image || '',
            description: e.description || '',
            registrationStatus: e.registration_status || e.registrationStatus || 'open'
          }));
          setEvents(mappedEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);
  const stats = [
    {
      label: 'Total Events Organized',
      value: '156+',
      icon: 'CalendarDaysIcon',
      color: 'bg-primary'
    },
    {
      label: 'Lives Touched',
      value: '12,500+',
      icon: 'HeartIcon',
      color: 'bg-secondary'
    },
    {
      label: 'Active Volunteers',
      value: '450+',
      icon: 'UserGroupIcon',
      color: 'bg-accent'
    },
    {
      label: 'Community Partners',
      value: '35+',
      icon: 'BuildingOfficeIcon',
      color: 'bg-supporting'
    }];


  const filteredEvents = events.filter((event) => {
    const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
    const matchesLocation = selectedLocation === 'all' || event.location.toLowerCase().includes(selectedLocation.toLowerCase());
    const matchesStatus =
      selectedStatus === 'all' ||
      (selectedStatus === 'open' && event.registrationStatus === 'open') ||
      (selectedStatus === 'upcoming' && new Date(event.date).setHours(0,0,0,0) >= new Date().setHours(0,0,0,0)) ||
      (selectedStatus === 'past' && new Date(event.date).setHours(0,0,0,0) < new Date().setHours(0,0,0,0));
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesLocation && matchesStatus && matchesSearch;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    const now = new Date().setHours(0, 0, 0, 0);

    const isAUpcoming = dateA >= now;
    const isBUpcoming = dateB >= now;

    if (isAUpcoming && isBUpcoming) return dateA - dateB; // Both upcoming: soonest first
    if (!isAUpcoming && !isBUpcoming) return dateB - dateA; // Both past: most recent first
    return isAUpcoming ? -1 : 1; // Upcoming before past
  });

  const upcomingEvents = events.
    filter((event) => new Date(event.date) > new Date()).
    sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).
    slice(0, 5);

  const calendarEvents = events.map((event) => ({
    id: event.id,
    title: event.title,
    date: event.date,
    category: event.category
  }));

  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) =>
                <div key={i} className="h-32 bg-muted rounded"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary via-primary/90 to-secondary py-10 sm:py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-primary-foreground mb-6">
                Community Events & Programs
              </h1>
              <p className="text-lg lg:text-xl text-primary-foreground/90 mb-8">
                Join us in making a difference. Participate in health camps, awareness drives, and community celebrations that bring positive change to lives.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="#upcoming-events"
                  className="w-full sm:w-auto px-8 py-4 rounded-lg font-heading font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-300 shadow-cta">

                  Browse Events
                </a>
                <a
                  href="/get-involved"
                  className="w-full sm:w-auto px-8 py-4 rounded-lg font-heading font-semibold bg-white text-primary hover:bg-white/90 transition-all duration-300 shadow-cta">

                  Become a Volunteer
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-card">
          <div className="container mx-auto px-4">
            <EventStats stats={stats} />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12" id="upcoming-events">
          <div className="container px-4">
            {/* Search, Filter Toggle, and View Toggle */}
            <div className="mb-6 flex w-auto flex-col gap-3">
              <div className="flex justify-center sm:flex-row gap-3">
                <div className="relative flex-1 max-w-xl">
                  <Icon
                    name="MagnifyingGlassIcon"
                    size={20}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" />

                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />

                </div>
                <div className="flex items-center gap-2">
                  {/* Mobile filters toggle */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`lg:hidden flex items-center space-x-2 px-4 py-3 rounded-lg border border-border bg-card text-foreground font-medium text-sm transition-colors ${showFilters ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    <Icon name="FunnelIcon" size={18} />
                    <span>Filters</span>
                    {(selectedCategory !== 'all' || selectedLocation !== 'all' || selectedStatus !== 'all') && (
                      <span className="bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">!</span>
                    )}
                  </button>
                  <div className="flex items-center space-x-2 bg-card rounded-lg p-1 border border-border">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`
                      }>
                      <Icon name="Squares2X2Icon" size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode('calendar')}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${viewMode === 'calendar' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`
                      }>
                      <Icon name="CalendarIcon" size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
              {/* Filters Sidebar — hidden on mobile by default, toggled by button */}
              <div className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden'} lg:block`}>
                <EventFilters
                  selectedCategory={selectedCategory}
                  selectedLocation={selectedLocation}
                  selectedStatus={selectedStatus}
                  onCategoryChange={setSelectedCategory}
                  onLocationChange={setSelectedLocation}
                  onStatusChange={setSelectedStatus} />

                <div className="mt-6">
                  <UpcomingEventsList events={upcomingEvents} />
                </div>
              </div>

              {/* Events Display */}
              <div className="lg:col-span-3">
                {viewMode === 'grid' ?
                  <>
                    <div className="mb-6 flex items-center justify-between">
                      <h2 className="text-2xl font-heading font-bold text-foreground">
                        {sortedEvents.length} Events Found
                      </h2>
                    </div>
                    {sortedEvents.length > 0 ?
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {sortedEvents.map((event) =>
                          <EventCard key={event.id} event={event} />
                        )}
                      </div> :

                      <div className="text-center py-16">
                        <Icon name="CalendarDaysIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                          No Events Found
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          Try adjusting your filters or search query
                        </p>
                        <button
                          onClick={() => {
                            setSelectedCategory('all');
                            setSelectedLocation('all');
                            setSelectedStatus('all');
                            setSearchQuery('');
                          }}
                          className="px-6 py-3 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300">

                          Clear All Filters
                        </button>
                      </div>
                    }
                  </> :

                  <EventCalendar events={calendarEvents} />
                }
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-16 bg-gradient-to-br from-accent via-accent/90 to-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl lg:text-4xl font-heading font-bold text-accent-foreground mb-4 sm:mb-6">
                Want to Organize an Event?
              </h2>
              <p className="text-lg text-accent-foreground/90 mb-6 sm:mb-8">
                Partner with us to create meaningful community events. We provide support, volunteers, and resources to make your initiative successful.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="/get-involved"
                  className="w-full sm:w-auto px-8 py-4 rounded-lg font-heading font-semibold bg-white text-accent hover:bg-white/90 transition-all duration-300 shadow-cta">
                  Partner With Us
                </a>
                <a
                  href="/gallery"
                  className="w-full sm:w-auto px-8 py-4 rounded-lg font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta">
                  View Past Events
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default EventsInteractive;